import { Enum, EnumValues } from "./Enum"

export type Category<E extends Enum, C extends EnumValues<E> = EnumValues<E>> = {
    id: C
    name: string
    description: string
}

export type CategoryDefinitions<E extends Enum> = {
    [C in EnumValues<E>]: Category<E, C>
}

export type CategoryDefinitionsWithoutId<E extends Enum> = {
    [C in EnumValues<E>]: Omit<Category<E, C>, 'id'>
}

type WithId<T> = {
    [I in keyof T]: {
        [C in keyof T[I]as `${string & I}_${string & C}`]: {
            id: `${string & I}_${string & C}`
        } & T[I][C]
    }
}

type ShallowWithId<T> = {
    [C in keyof T]: {
        id: C
    } & T[C]
}

export type GetValuesInArray<T> = T extends Array<infer F> ? F : never

export type GetObjectMatchingId<O, I> = O extends { id: I } ? O : never

export type SecondLayerKeys<T> = { [H in { [K in keyof T]: keyof T[K] }[keyof T] & string]: H }

export type NestedEnumize<T> = {
    [K in keyof T]: {
        [C in keyof T[K]as `${string & K}_${string & C}`]: `${string & K}_${string & C}`
    }
}

export type FilterBy<O extends Record<string, any>, M extends keyof O> = {
    [I in Extract<keyof O, M>]: O[I]
}

export type NestedFlatten<T> = { [K in keyof T]: T[K][keyof T[K]] }[keyof T]

export type Flatten<O extends Record<string, any>, M extends keyof O> = {
    [P in keyof SecondLayerKeys<FilterBy<O, M>>]: GetObjectMatchingId<NestedFlatten<O>, P>
}

function enumFromKeys<T>(map: T) {
    return Object.keys(map).reduce((acc, key) => ({
        ...acc,
        [key]: key
    }), {} as { [K in keyof T]: K })
}

function enumFromSecondLayerKeys<T>(map: T) {
    return Object.keys(map).reduce((acc, key) => ({
        ...acc,
        [key]: enumFromKeys((map as any)[key])
    }), {} as { [K in keyof T]: { [C in keyof T[K]]: C } })
}

export function makeShallowDefinition<T>(definitions: T) {
    const Flat: { [K in keyof ShallowWithId<T>]: ShallowWithId<T>[K] } =
        Object.entries(definitions).reduce((acc, [category, description]) => ({
            ...acc,
            [category]: { id: category, ...(description as any) },
        }),
            {} as any,
        )

    const FlatList: ShallowWithId<T>[keyof ShallowWithId<T>][] = Object.values(Flat)

    const FlatEnum = enumFromKeys(Flat)
    const FlatEnumList: (keyof typeof FlatEnum)[] = Object.values(FlatEnum)

    return {
        Flat,
        FlatList,
        FlatEnum,
        FlatEnumList,
    }
}

/*
    type s = typeof FlatList

    type y<T> = T extends Array<infer F> ? F : never

    type u = y<s>

    type k<T, O> = T extends { id: O } ? T : never

    type o = {
        [P in keyof typeof FlatEnum]: k<u, P>
    }
*/

export function makeDefinition<T>(definitionTree: T) {
    const Grouped: { [K in keyof WithId<T>]: WithId<T>[K] } =
        Object.entries(definitionTree).reduce((acc, [industryType, categories]) => ({
            ...acc,
            [industryType]: Object.entries(categories).reduce(
                (acc, [category, description]) => ({ ...acc, [`${industryType}_${category}`]: { id: `${industryType}_${category}`, ...(description as any) }, }),
                {} as any),
        }),
            {} as any,
        )

    const GroupedList: { [K in keyof WithId<T>]: WithId<T>[K][keyof WithId<T>[K]][] } =
        Object.entries(Grouped).reduce((acc, [industryType, categories]) => ({
            ...acc,
            [industryType]: Object.values(categories as any),
        }),
            {} as any,
        )

    const GroupedEnum = enumFromSecondLayerKeys(Grouped)

    const GroupedEnumList: { [K in keyof typeof GroupedEnum]: typeof GroupedEnum[K][keyof typeof GroupedEnum[K]][] } =
        Object.entries(GroupedEnum).reduce((acc, [industryType, categories]) => ({
            ...acc,
            [industryType]: Object.values(categories as any),
        }),
            {} as any,
        )

    /*Flat: { [K in keyof WithId<T>]: WithId<T>[K][keyof WithId<T>[K]] }*/

    // const Flat: { [K in keyof WithId<T> as keyof WithId<T>[K]]: WithId<T>[K][keyof WithId<T>[K]] } =
    const Flat: {
        [P in keyof typeof FlatEnum]: GetObjectMatchingId<GetValuesInArray<typeof FlatList>, P>
    } = Object.values(Grouped).reduce((acc: any, categories: any) => ({
        ...acc,
        ...categories,
    }), {} as any) as any

    const FlatList: { [K in keyof WithId<T>]: WithId<T>[K][keyof WithId<T>[K]] }[keyof WithId<T>][] = Object.values(Flat) as any

    const FlatEnum: SecondLayerKeys<typeof GroupedEnum>/*{ [K in keyof WithId<T>]: WithId<T>[K] }[keyof WithId<T>]*/ = enumFromKeys(Flat) as any
    const FlatEnumList: { [K in keyof typeof GroupedEnum]: typeof GroupedEnum[K][keyof typeof GroupedEnum[K]] }[keyof typeof GroupedEnum][] = Object.values(FlatEnum)

    return {
        Flat,
        FlatList,
        FlatEnum,
        FlatEnumList,
        Grouped,
        GroupedList,
        GroupedEnum,
        GroupedEnumList,
    }
}
